import { z } from "zod";

export type ScoreWithStatistics = {
    value: ScoreValue;
    avg: ScoreValue;
    min: ScoreValue;
    max: ScoreValue;
    count: ScoreValue;
    prev: ScoreValue;
};
export type ScoreValue = number | null;
export type ScoreSkeleton = ScoreWithStatistics | ScoreValue;

export type ScoreVariation = {
    id: number;
    label: string;
    unit: string;
    default_display: boolean;
};

export type ScoreDimensionBaseValue<ScoreSkeleton = ScoreValue> = {
    score: ScoreSkeleton;
    label: string;
    position: number | null;
};

export type ScoreDimensionValue<ScoreSkeleton = ScoreValue> = ScoreDimensionBaseValue<ScoreSkeleton> & {
    dimensions?: ScoreDimension<ScoreSkeleton>[];
};

export type ScoreDimension<ScoreSkeleton = ScoreValue> = {
    label: string;
    values: Record<string, ScoreDimensionValue<ScoreSkeleton>>;
};

export type Score<ScoreSkeleton = ScoreValue> = {
    score: ScoreSkeleton;
    unit: string;
    label: string;
    default_display: boolean;
    variations: ScoreVariation[];
    dimensions: ScoreDimension<ScoreSkeleton>[];
};

export type Statistic = Partial<Record<string, Score | undefined>>;
export type Statistics = Record<string, Statistic>;

export type Scores = Partial<{ [scoreId: string]: Score }>;

export const doesScoreExist = (score?: Score): score is Score & { score: number } => {
    return score !== undefined && score.score !== null;
};

export const ScoreVariationSchema = z.object({
    id: z.number(),
    label: z.string(),
    unit: z.string(),
    default_display: z.boolean(),
});

export const ScoreDimensionBaseValueSchema = z.object({
    score: z.number().nullable(),
    label: z.string(),
    position: z.number().nullable(),
});

export const ScoreDimensionValueSchema: z.ZodType<ScoreDimensionValue> = ScoreDimensionBaseValueSchema.extend({
    dimensions: z.array(z.lazy(() => ScoreDimensionSchema)).optional(),
});

export const ScoreDimensionSchema = z.object({
    label: z.string(),
    values: z.record(z.string(), ScoreDimensionValueSchema),
});

export const ScoreSchema = z
    .object({
        score: z.number().nullable(),
        unit: z.string(),
        label: z.string(),
        default_display: z.boolean(),
        variations: ScoreVariationSchema.array(),
        dimensions: ScoreDimensionSchema.array(),
    })
    .transform((data) => {
        if (data.variations) {
            const uniqueIds = new Set<number>();
            const uniqueVariations = data.variations.filter((v) => {
                if (uniqueIds.has(v.id)) return false;
                uniqueIds.add(v.id);
                return true;
            });
            return {
                ...data,
                variations: uniqueVariations,
            };
        }
        return data;
    });

export const ScoresSchema = z.record(z.string(), ScoreSchema);

// const score = {
//     "XX" : {
//         score: 11,
//         unit: "mPt/kg",
//         label: "par kg de produit"
//         default_display: true,
//         dimensions: [],
//         variations: [
//             { id: 1, default_display: true, label: "" }
//         ]
//     },
// }

// const LastLayerDimensionScore = {
//     label: "dimensionLayerName",
//     values: {
//         199: {
//             label: "human-readable string",
//             score: 1212,
//             position: 1,
//         }
//     }
// }

// const DimensionScoreWithInnerLayers = {
//     label: "dimensionLayerName",
//     values: {
//         199: {
//             label: "human-readable string",
//             score: 1212,
//             position: 1,
//             dimensions: [
//                 LastLayerDimensionScore,
//             ]
//         }
//     }
// }

// const scoresWithDimensions = {
//     "XX": {
//         unit: "mPt/kg",
//         score: 11,

//         // label is human-readable string;
//         label: "par kg de produits",
//         default_display: true,
//         dimensions: [ DimensionScore ],
//         variations: [
//             { id: 1, default_display: true, label: "" },
//         ],
//     }
// }
